import React, { useMemo } from 'react';

// Motivational messages organized by context
const MESSAGES = {
  // For users who haven't started their day yet (0% progress)
  startYourDay: [
    { text: "Every expert was once a beginner. Start your day strong!", emoji: "🚀" },
    { text: "Small steps every day lead to big results. Let's get started!", emoji: "🌱" },
    { text: "Your future self will thank you for the habits you build today.", emoji: "✨" },
    { text: "Success is the sum of small efforts repeated day in and day out.", emoji: "💪" },
    { text: "The secret of getting ahead is getting started.", emoji: "🎯" },
  ],

  // For users making progress (1-49%)
  keepGoing: [
    { text: "You're making progress! Keep the momentum going!", emoji: "🔥" },
    { text: "Every action counts. You're building something great!", emoji: "🏗️" },
    { text: "Progress, not perfection. You've got this!", emoji: "⭐" },
    { text: "Consistency beats intensity. Keep showing up!", emoji: "💎" },
    { text: "You're one step closer to your goals. Don't stop now!", emoji: "🏃" },
  ],

  // For users over halfway (50-99%)
  almostThere: [
    { text: "You're over halfway there! Finish strong!", emoji: "💪" },
    { text: "The finish line is in sight. You've got this!", emoji: "🏁" },
    { text: "Champions keep going until they get it right. Almost there!", emoji: "🏆" },
    { text: "You're crushing it today! Just a little more!", emoji: "⚡" },
    { text: "Success is just around the corner. Keep pushing!", emoji: "🌟" },
  ],

  // For users who completed all daily habits (100%)
  perfectDay: [
    { text: "Perfect day! You've mastered today's habits!", emoji: "🎉" },
    { text: "Outstanding! You hit all your targets today!", emoji: "🏆" },
    { text: "You're on fire! All daily goals achieved!", emoji: "🔥" },
    { text: "Excellence in action! You completed everything!", emoji: "⭐" },
    { text: "Today you were unstoppable! Great work!", emoji: "💪" },
  ],

  // For users with active streaks
  streakMessages: [
    { text: "Your streak is building momentum! Keep it alive!", emoji: "🔥" },
    { text: "Consistency is your superpower. Keep the streak going!", emoji: "⚡" },
    { text: "You're building a powerful habit loop!", emoji: "🔄" },
  ],

  // For users with no streak yet
  startStreak: [
    { text: "Start a streak today! Consistency creates champions.", emoji: "🎯" },
    { text: "Your streak journey begins with today's first action.", emoji: "🌱" },
    { text: "Every streak starts with day one. Make today count!", emoji: "1️⃣" },
  ],
};

// Get a deterministic "random" index based on the date
// This ensures the message changes each day but is consistent for the same day
function getMessageIndex(messages, dateStr, userId) {
  // Use date + userId to create a deterministic but varying selection
  const hash = (dateStr + userId).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return hash % messages.length;
}

export default function MotivationalMessage({ progressPercent, streak, userId, dateStr }) {
  const message = useMemo(() => {
    let messages;

    // Select message category based on progress
    if (progressPercent === 100) {
      messages = MESSAGES.perfectDay;
    } else if (progressPercent >= 50) {
      messages = MESSAGES.almostThere;
    } else if (progressPercent > 0) {
      messages = MESSAGES.keepGoing;
    } else {
      // 0% progress - check if they have a streak or need to start one
      if (streak && streak.current_streak > 0) {
        messages = MESSAGES.streakMessages;
      } else {
        messages = MESSAGES.startStreak;
      }
    }

    // Get deterministic message based on date and user
    const index = getMessageIndex(messages, dateStr, userId || 'default');
    return messages[index];
  }, [progressPercent, streak, userId, dateStr]);

  return (
    <div
      className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4 mb-6 border border-primary/20"
      data-testid="motivational-message"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl" data-testid="motivational-emoji">{message.emoji}</span>
        <p className="text-sm md:text-base font-medium text-gray-700 dark:text-gray-100" data-testid="motivational-text">
          {message.text}
        </p>
      </div>
    </div>
  );
}
